/**
 * editor-render-strategy.ts — the render-strategy seam behind the editable
 * `/editor` picture-render port (pdfjs-editor-removal-cutover Phase 2, chunk 2a).
 *
 * WHAT. `editor-render.ts` historically produced two things per page from pdf.js:
 * the RASTER (a `page.render()` into a `<canvas>`) and the page NATURAL DIMENSIONS
 * (`page.getViewport({scale:1})`, which the overlay coordinate frame keys off).
 * When {@link EDITOR_PICTURE_RENDER_FLAG} is ON this module supplies BOTH from the
 * backend instead: it fetches the page PNG from
 * `GET /api/pdf-page-image/<jobId>/<page>?dpi=192` — the same route + client
 * discipline as the read-only `pictures-not-pages` pilot's `PdfPageImageCache` —
 * and derives the natural dims FROM THAT RASTER.
 *
 * WHY derive dims from the raster. The render service renders the CropBox region
 * (`use_cropbox=True`, phase-2b G-CROP) with page `/Rotate` applied, so the PNG's
 * pixel dimensions ARE the C4-correct rotation-swapped CropBox frame that
 * `web/a11yvalidate/render.py:_pdf_canvas_dims_px` computes server-side. Deriving
 * `naturalWidthPts = pngWidthPx * 72 / dpi` therefore keys the overlay frame off
 * the EXACT raster the overlays sit on — the strongest possible registration
 * guarantee, which is precisely what the fail-quiet C4 golden gate exists to catch.
 *
 * CAVEAT C1 (lazy per-page — never re-fetch the whole doc). A page's PNG is fetched
 * on demand and memoized; opening a 300-page PDF does NOT fire 300 requests.
 * In-flight fetches are de-duped per page.
 *
 * All environment seams (`fetch`, image decode) are INJECTABLE so the module is
 * import-safe + unit-testable without a DOM. No DOM globals are touched at module
 * scope.
 *
 * @module editor/editor-render-strategy
 */
import { EDITOR_PICTURE_RENDER_FLAG, editorPictureRenderEnabledByGlobal, } from "./pilot-flags.js";
export { EDITOR_PICTURE_RENDER_FLAG };
/**
 * The HiDPI rung the port requests (pdfjs-editor-removal-cutover Phase 2 fold F1):
 * the editor renders at `devicePixelRatio` × 0.5–2× zoom, so a 96-dpi raster
 * upscales ~4× at the 200%×dpr=2 worst case. 192 is the F1 default the C2 audit
 * (chunk 2c) may raise. MUST be a rung in `EDITOR_PAGE_IMAGE_DPI_RUNGS`
 * (`web/a11yvalidate/render.py`) — the route snaps to the nearest rung regardless.
 */
export const EDITOR_PICTURE_RENDER_DPI = 192;
/** Points-per-inch — a PDF user-space unit is 1/72 inch, so points = px * 72 / dpi. */
const POINTS_PER_INCH = 72;
/** True when the editable-editor picture-render port is enabled (default OFF). */
export function editorPictureRenderEnabled(win) {
    return editorPictureRenderEnabledByGlobal(win);
}
const DEFAULT_ROUTE_PREFIX = "/api/pdf-page-image";
/**
 * Lazy, per-page, memoizing raster cache for the editable-editor picture-render
 * port. One instance is held module-private in `editor-render.ts`; it fetches +
 * decodes a page's backend PNG on first demand and derives the C4-correct natural
 * frame from the decoded pixel dims. Caveat C1: never the whole doc.
 */
export class EditorPageRasterCache {
    doFetch;
    decode;
    routePrefix;
    dpi;
    /** `<jobId>\n<pageNum>` → resolved frame (memoized; success only). */
    frames = new Map();
    /** `<jobId>\n<pageNum>` → in-flight fetch (dedupe concurrent approaches). */
    inflight = new Map();
    constructor(opts = {}) {
        this.doFetch = opts.fetchImpl ?? defaultFetch;
        this.decode = opts.decodeImpl ?? defaultDecode;
        this.routePrefix = opts.routePrefix ?? DEFAULT_ROUTE_PREFIX;
        this.dpi = opts.dpi ?? EDITOR_PICTURE_RENDER_DPI;
    }
    key(jobId, pageNum) {
        return `${jobId}\n${pageNum}`;
    }
    /** The backend page-image URL for a (job, page) at the port's DPI (page is 1-based). */
    urlFor(jobId, pageNum) {
        return `${this.routePrefix}/${encodeURIComponent(jobId)}/${pageNum}?dpi=${this.dpi}`;
    }
    /** Synchronous read of an already-fetched frame's dims (null if not yet cached). */
    cachedNaturalDims(jobId, pageNum) {
        const frame = this.frames.get(this.key(jobId, pageNum));
        return frame ? { width: frame.naturalWidthPts, height: frame.naturalHeightPts } : null;
    }
    /**
     * Fetch + decode a page's raster (lazy, memoized, in-flight-deduped). Resolves
     * to null on any miss (network error, non-OK response, empty/undecodable body)
     * so the caller can fall back to the pdf.js render path rather than blanking
     * the page. Caveat C1: fetches ONLY the requested page.
     */
    ensurePageRaster(jobId, pageNum) {
        const k = this.key(jobId, pageNum);
        const cached = this.frames.get(k);
        if (cached)
            return Promise.resolve(cached);
        const existing = this.inflight.get(k);
        if (existing)
            return existing;
        const promise = this.fetchFrame(jobId, pageNum, k);
        this.inflight.set(k, promise);
        return promise;
    }
    async fetchFrame(jobId, pageNum, k) {
        try {
            const resp = await this.doFetch(this.urlFor(jobId, pageNum), {});
            if (!resp.ok)
                return null;
            const blob = await resp.blob();
            if (!blob || blob.size === 0)
                return null;
            const img = await this.decode(blob);
            const wPx = img.naturalWidth || img.width;
            const hPx = img.naturalHeight || img.height;
            if (wPx <= 0 || hPx <= 0)
                return null;
            const frame = {
                img,
                naturalWidthPts: (wPx * POINTS_PER_INCH) / this.dpi,
                naturalHeightPts: (hPx * POINTS_PER_INCH) / this.dpi,
            };
            this.frames.set(k, frame);
            return frame;
        }
        catch {
            // Network / decode error — return null so the caller falls back to
            // pdf.js. A later approach re-fetches (still lazy per caveat C1).
            return null;
        }
        finally {
            this.inflight.delete(k);
        }
    }
}
// ── Default environment seams (used when not injected) ────────────────────────
function defaultFetch(input, init) {
    if (typeof fetch === "undefined") {
        return Promise.reject(new Error("EditorPageRasterCache: no global fetch — inject fetchImpl"));
    }
    return fetch(input, init);
}
function defaultDecode(blob) {
    if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function"
        || typeof Image === "undefined") {
        return Promise.reject(new Error("EditorPageRasterCache: no Image/URL — inject decodeImpl"));
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => { resolve(img); };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("EditorPageRasterCache: image decode failed"));
        };
        img.src = url;
    });
}
